import { useCallback, useEffect, useRef } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { api } from '../lib/api';
import { runCartMutation } from '../lib/cart-mutation-queue';
import type { Cart, CartItem } from '../lib/api';
import { useCounters } from './useCounters';
import { useRegion } from './useRegion';
import {
  findCartLineForProduct,
  mergeCartItem,
  patchCartQuantity,
} from '../utils/cartProduct';

const EMPTY_CART: Cart = {
  items: [],
  subtotal: 0,
  item_count: 0,
  is_empty: true,
};

function isCartSwrKey(key: unknown): key is string {
  return typeof key === 'string' && (key === '/api/cart' || key.startsWith('/api/cart?'));
}

export function useCart() {
  const { refreshCartCount, setCartCount } = useCounters();
  const { getRegionId } = useRegion();
  const regionIdRef = useRef<number | null>(null);
  regionIdRef.current = getRegionId();

  const regionId = regionIdRef.current;
  const cartKey = regionId ? `/api/cart?region_id=${regionId}` : '/api/cart';

  const { data: cart, error, isLoading, mutate: mutateCart } = useSWR(
    cartKey,
    () => api.cart.get(regionId ? { region_id: regionId } : undefined),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateIfStale: false,
      keepPreviousData: true,
      dedupingInterval: 5000,
    },
  );

  useEffect(() => {
    if (typeof cart?.item_count === 'number') {
      setCartCount(cart.item_count);
    }
  }, [cart?.item_count, setCartCount]);

  const syncCartFromServer = useCallback(async () => {
    const rid = regionIdRef.current;
    const fresh = await api.cart.get(rid ? { region_id: rid } : undefined);
    await mutateCart(fresh, { revalidate: false });
    return fresh;
  }, [mutateCart]);

  const applyCartToCache = useCallback(
    async (updater: (current: Cart | undefined) => Cart | null | undefined) => {
      return mutateCart(
        (current) => {
          const next = updater(current);
          if (next === undefined || next === null) return current;
          return next;
        },
        { revalidate: false },
      );
    },
    [mutateCart],
  );

  const addToCart = useCallback(
    (
      productId: number,
      quantity: number = 1,
      variationAttributes?: { attribute_slug: string; value_slug: string }[],
      preview?: Partial<CartItem>,
    ) =>
      runCartMutation(async () => {
        await applyCartToCache((current) =>
          patchCartQuantity(current ?? null, productId, quantity, preview),
        );

        try {
          const rid = regionIdRef.current;
          const result = await api.cart.add({
            product_id: productId,
            quantity,
            region_id: rid ?? undefined,
            ...(variationAttributes?.length ? { variation_attributes: variationAttributes } : {}),
          });

          await applyCartToCache((current) =>
            mergeCartItem(current ?? null, result.item),
          );

          return result;
        } catch (err) {
          await syncCartFromServer();
          throw err;
        }
      }),
    [applyCartToCache, syncCartFromServer],
  );

  /**
   * Реальный id строки на сервере (Vanilo при update/add пересоздаёт item → id меняется).
   * Ищем по product_id, затем по переданному itemId.
   */
  const resolveServerLineId = useCallback(
    async (itemId: number, productId?: number): Promise<{ serverItemId: number; productId: number } | null> => {
      let resolvedProductId = productId;
      let serverItemId: number | undefined;

      await applyCartToCache((current) => {
        const byProduct =
          resolvedProductId != null
            ? findCartLineForProduct(current, resolvedProductId)
            : undefined;
        const byId = current?.items?.find((i) => i.id === itemId);
        const line = byProduct ?? byId;
        if (line) {
          resolvedProductId = line.product_id;
          if (line.id > 0) serverItemId = line.id;
        }
        return current;
      });

      if (resolvedProductId == null) return null;

      if (!serverItemId) {
        const fresh = await syncCartFromServer();
        const line = findCartLineForProduct(fresh, resolvedProductId);
        if (!line || line.id <= 0) return null;
        return { serverItemId: line.id, productId: resolvedProductId };
      }

      return { serverItemId, productId: resolvedProductId };
    },
    [applyCartToCache, syncCartFromServer],
  );

  const updateQuantity = useCallback(
    (itemId: number, quantity: number, productId?: number) =>
      runCartMutation(async () => {
        let resolvedProductId = productId;

        await applyCartToCache((current) => {
          const line =
            (resolvedProductId != null
              ? findCartLineForProduct(current, resolvedProductId)
              : undefined) ??
            current?.items?.find((i) => i.id === itemId);
          if (!line) return current;
          resolvedProductId = line.product_id;
          const delta = quantity - line.quantity;
          if (delta === 0) return current;
          return patchCartQuantity(current ?? null, line.product_id, delta);
        });

        const resolved = await resolveServerLineId(itemId, resolvedProductId);
        if (!resolved) {
          await syncCartFromServer();
          const err = Object.assign(new Error('Товар не найден в корзине'), {
            status: 404,
            data: { message: 'Товар не найден в корзине' },
          });
          throw err;
        }

        try {
          const rid = regionIdRef.current;
          const result = await api.cart.update(resolved.serverItemId, {
            quantity,
            region_id: rid ?? undefined,
          });
          await applyCartToCache((current) =>
            mergeCartItem(current ?? null, result.item),
          );
          return result;
        } catch (err) {
          await syncCartFromServer();
          throw err;
        }
      }),
    [applyCartToCache, resolveServerLineId, syncCartFromServer],
  );

  const updateVariant = useCallback(
    (
      itemId: number,
      quantity: number,
      variationAttributes?: { attribute_slug: string; value_slug: string }[],
      productId?: number,
    ) =>
      runCartMutation(async () => {
        const resolved = await resolveServerLineId(itemId, productId);
        if (!resolved) {
          await syncCartFromServer();
          const err = Object.assign(new Error('Товар не найден в корзине'), {
            status: 404,
            data: { message: 'Товар не найден в корзине' },
          });
          throw err;
        }

        try {
          const rid = regionIdRef.current;
          const result = await api.cart.update(resolved.serverItemId, {
            quantity,
            region_id: rid ?? undefined,
            ...(variationAttributes?.length ? { variation_attributes: variationAttributes } : {}),
          });
          await applyCartToCache((current) =>
            mergeCartItem(current ?? null, result.item),
          );
          return result;
        } catch (err) {
          await syncCartFromServer();
          throw err;
        }
      }),
    [applyCartToCache, resolveServerLineId, syncCartFromServer],
  );

  const removeFromCart = useCallback(
    (itemId: number, productId?: number) =>
      runCartMutation(async () => {
        // Сначала реальный id: после optimistic-удаления строки в кэше уже нет
        const resolved = await resolveServerLineId(itemId, productId);
        if (!resolved) {
          await syncCartFromServer();
          return;
        }

        await applyCartToCache((current) =>
          patchCartQuantity(current ?? null, resolved.productId, -(
            findCartLineForProduct(current, resolved.productId)?.quantity ?? 0
          )),
        );

        try {
          await api.cart.remove(resolved.serverItemId);
          await syncCartFromServer();
        } catch (err) {
          await syncCartFromServer();
          throw err;
        }
      }),
    [applyCartToCache, resolveServerLineId, syncCartFromServer],
  );

  const clearCart = useCallback(
    () =>
      runCartMutation(async () => {
        await applyCartToCache(() => EMPTY_CART);

        try {
          await api.cart.clear();
        } catch (err) {
          await syncCartFromServer();
          throw err;
        }
      }),
    [applyCartToCache, syncCartFromServer],
  );

  /**
   * После оформления заказа сервер уже очистил корзину —
   * сбрасываем все локальные кэши SWR и счётчик в шапке.
   */
  const resetCartAfterOrder = useCallback(async () => {
    setCartCount(0);
    await mutateCart(EMPTY_CART, { revalidate: false });
    await globalMutate(isCartSwrKey, EMPTY_CART, { revalidate: false });
    try {
      await refreshCartCount();
      const rid = regionIdRef.current;
      const fresh = await api.cart.get(rid ? { region_id: rid } : undefined);
      await globalMutate(isCartSwrKey, fresh, { revalidate: false });
      if (typeof fresh.item_count === 'number') {
        setCartCount(fresh.item_count);
      }
    } catch {
      setCartCount(0);
    }
  }, [mutateCart, refreshCartCount, setCartCount]);

  /**
   * Изменить количество по product_id, читая актуальный кэш внутри очереди.
   * «+» идёт через POST /cart (сервер суммирует), «−» через PUT/DELETE с реальным item.id.
   */
  const adjustCartQuantity = useCallback(
    (
      productId: number,
      delta: number,
      preview?: Partial<CartItem>,
    ) =>
      runCartMutation(async () => {
        if (delta === 0) return;

        if (delta > 0) {
          await applyCartToCache((current) =>
            patchCartQuantity(current ?? null, productId, delta, preview),
          );

          try {
            const rid = regionIdRef.current;
            const result = await api.cart.add({
              product_id: productId,
              quantity: delta,
              region_id: rid ?? undefined,
            });
            await applyCartToCache((current) =>
              mergeCartItem(current ?? null, result.item),
            );
            return result;
          } catch (err) {
            await syncCartFromServer();
            throw err;
          }
        }

        // delta < 0
        let serverItemId: number | undefined;
        let newQty = 0;

        await applyCartToCache((current) => {
          const line = findCartLineForProduct(current ?? null, productId);
          if (!line || line.id <= 0) return current;
          serverItemId = line.id;
          newQty = line.quantity + delta;
          return patchCartQuantity(current ?? null, productId, delta);
        });

        if (!serverItemId) {
          await syncCartFromServer();
          return;
        }

        try {
          const rid = regionIdRef.current;
          if (newQty <= 0) {
            await api.cart.remove(serverItemId);
            await syncCartFromServer();
            return;
          }

          const result = await api.cart.update(serverItemId, {
            quantity: newQty,
            region_id: rid ?? undefined,
          });
          await applyCartToCache((current) =>
            mergeCartItem(current ?? null, result.item),
          );
          return result;
        } catch (err) {
          await syncCartFromServer();
          throw err;
        }
      }),
    [applyCartToCache, syncCartFromServer],
  );

  const loadCart = useCallback(async () => {
    await syncCartFromServer();
  }, [syncCartFromServer]);

  return {
    cart: cart || null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Ошибка загрузки корзины') : null,
    addToCart,
    adjustCartQuantity,
    updateQuantity,
    updateVariant,
    removeFromCart,
    clearCart,
    resetCartAfterOrder,
    reloadCart: loadCart,
  };
}
